import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { funilApi, ApiError } from '@/api/funil';
import {
  PACOTES_VENDAVEIS,
  PACOTE_DEFAULT,
  GATEWAYS_DISPONIVEIS,
  GATEWAY_DEFAULT,
} from '@/config/pacotes';
import { Button } from '@/components/atoms';

import styles from './Billing.module.css';

export interface BillingProps {
  initialEmpty?: boolean;
}

// O `?package=` vem de fora (link de e-mail, /planos, bookmark antigo) e o
// catálogo real não tem `pro`: valida contra a lista vendável e qualquer
// valor desconhecido cai no default em vez de virar 422 no backend.
function pacoteValido(bruto: string | null): string {
  return bruto && (PACOTES_VENDAVEIS as readonly string[]).includes(bruto)
    ? bruto
    : PACOTE_DEFAULT;
}

function capitalizar(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

export function Billing({ initialEmpty = false }: BillingProps = {}) {
  const { t } = useTranslation(['funil', 'common']);
  const [searchParams] = useSearchParams();

  const [selectedPackage, setSelectedPackage] = useState(() =>
    pacoteValido(searchParams.get('package'))
  );
  const [gateway, setGateway] = useState<string>(GATEWAY_DEFAULT);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEmpty] = useState(initialEmpty);

  async function handleAssinar() {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await funilApi.assinar({
        package: selectedPackage,
        gateway,
      });

      if (res.checkout_url && res.checkout_url.trim().length > 0) {
        window.location.assign(res.checkout_url);
        return;
      }

      setStatus(res.status);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(t('funil:billing.erro_generico'));
      } else {
        setError(t('funil:billing.erro_generico'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelar() {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await funilApi.cancelar();
      setStatus(res.status);
      setSuccessMessage(t('funil:billing.sucesso_cancelamento'));
    } catch (err) {
      // 502 = o PSP não confirmou; o backend garante que nada mudou.
      if (err instanceof ApiError && err.status === 502) {
        setError(t('funil:billing.erro_psp_nao_confirmou'));
      } else {
        setError(t('funil:billing.erro_generico'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleTrocarPacote(novoPacote: string) {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await funilApi.trocarPacote({ package: novoPacote });
      if (res.checkout_url && res.checkout_url.trim().length > 0) {
        window.location.assign(res.checkout_url);
        return;
      }
      setSelectedPackage(res.package || novoPacote);
      setStatus(res.status);
      setSuccessMessage(t('funil:billing.sucesso_troca'));
    } catch (err) {
      // 502 = o PSP não confirmou; o backend garante que nada mudou.
      if (err instanceof ApiError && err.status === 502) {
        setError(t('funil:billing.erro_psp_nao_confirmou'));
      } else {
        setError(t('funil:billing.erro_generico'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t('funil:billing.titulo')}</h1>
          <p className={styles.subtitle}>{t('funil:billing.subtitulo')}</p>
        </div>

        {loading && (
          <div className={styles.loading}>
            {t('funil:billing.carregando')}
          </div>
        )}

        {error && (
          <div className={styles.errorAlert}>
            {error}
          </div>
        )}

        {successMessage && (
          <div className={styles.successAlert}>
            {successMessage}
          </div>
        )}

        {isEmpty && (
          <div className={styles.emptyCard}>
            <h2>{t('funil:billing.vazio_titulo')}</h2>
            <p>{t('funil:billing.vazio_descricao')}</p>
          </div>
        )}

        {status && (
          <div className={styles.card}>
            <span className={styles.sectionTitle}>
              {t('funil:billing.status_rotulo')} {status}
            </span>
          </div>
        )}

        {status === 'pending_payment' && (
          <div className={styles.pendingCard}>
            <h3 className={styles.pendingTitle}>
              {t('funil:billing.status_pending_payment')}
            </h3>
            <p>{t('funil:billing.instrucoes_pagamento_pendente')}</p>
            <p>{t('funil:billing.proximo_passo')}</p>
          </div>
        )}

        <div className={styles.card}>
          {/*
            F-42: "Pagamento Manual" abre a cobrança e devolve a instrução, mas
            quem ATIVA é POST /billing/webhook/manual autenticado como staff —
            nenhuma linha do front chama essa rota, e o recibo por e-mail
            (NotificationsService.send_payment_receipt) não tem chamador. A
            ressalva fica colada ao seletor e SEMPRE visível (não só quando
            'manual' está escolhido): a opção está sempre na lista, então o
            aviso também tem que estar. Fica FORA do <label> de propósito —
            dentro dele o texto entraria no nome acessível do <select>.
          */}
          <div className={styles.fieldGroup}>
            <label className={styles.field}>
              <span>{t('funil:billing.gateway_rotulo')}</span>
              <select
                value={gateway}
                onChange={(e) => setGateway(e.target.value)}
              >
                {GATEWAYS_DISPONIVEIS.map((g) => (
                  <option key={g} value={g}>
                    {g === 'manual'
                      ? t('funil:billing.gateway_manual')
                      : capitalizar(g)}
                  </option>
                ))}
              </select>
            </label>
            <p className={styles.hint}>{t('funil:billing.gateway_manual_aviso')}</p>
          </div>

          <label className={styles.field}>
            <span>{t('funil:planos.titulo')}</span>
            <select
              value={selectedPackage}
              onChange={(e) => setSelectedPackage(e.target.value)}
            >
              {PACOTES_VENDAVEIS.map((p) => (
                <option key={p} value={p}>
                  {capitalizar(p)}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.actions}>
            <Button
              variant="primary"
              loading={loading}
              onClick={handleAssinar}
            >
              {t('funil:billing.assinar_botao')}
            </Button>

            <Button
              variant="secondary"
              loading={loading}
              onClick={() => handleTrocarPacote(selectedPackage)}
            >
              {t('funil:billing.trocar_pacote_botao')}
            </Button>

            <Button
              variant="danger"
              loading={loading}
              onClick={handleCancelar}
            >
              {t('funil:billing.cancelar_botao')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
